import * as React from 'react'

import {
  getResourceControlsForRecord,
  isProjectLocationRecord,
  resourceTypeSupportsControls,
} from '~/client/src/shared/components/ResourceControls/controlKeys'
import { ResourceControlsTabProvider } from '~/client/src/shared/components/ResourceControls/ResourceControlsTabProvider'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import {
  controlsSection,
  generalResourceSection,
  withDefaultExpanded,
} from '~/client/src/shared/components/ResourceViewEdit/resourceEditSectionBuilders'
import { IControlFields } from '~/client/src/shared/hooks/useResourceControls'
import { IResourceRuntimeContext } from '~/client/src/shared/resources/definitions'
import type { IResourceControlCommands } from '~/client/src/shared/resources/domain/resourceControls'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'

import { ResourceEditRecord } from '../ResourceViewEdit/ResourceEditContext'

interface IProps {
  recordId: string
  record: ResourceEditRecord | null
  templateId?: string
  resourceTypeId: string
  runtimeContext: IResourceRuntimeContext
  projectId?: string
  qrCodeUrl?: string | null
  isCreationMode: boolean
  /** The resource's saved controls, read off the selected record's `root`. */
  controlFields: IControlFields
  /**
   * The control commands for THIS resource type. Passed in rather than resolved here:
   * the body is resource-agnostic, and reaching for a named store hook meant every type
   * that is not that one saved its controls through the wrong store.
   */
  controlCommands: IResourceControlCommands
  onClose: () => void
  onSave: (formData: Record<string, any>) => Promise<void>
  /** Creation keeps the card open after Save; the host then shows the saved record. */
  keepOpenOnCreate?: boolean
  onTemplateChange?: (templateId: string) => void
  /** Rendered in creation mode (the form/template picker). */
  formSelector?: React.ReactNode
  /** Delete the resource; shows the header's 3-dot overflow menu when provided. */
  onDelete?: () => void
  /**
   * Which section opens expanded by default (edit mode): `general` from the
   * objects table, `controls` from the controls table. Creation always expands
   * `general` (the only visible section).
   */
  defaultExpandedSectionId?: string
  resourceSchema: ResourceSchema
}

/**
 * Shared edit body for resources without a StruxTag link (Materials today).
 * Renders the consolidated sections card: a Resource properties (form) section
 * plus — for controls-enabled types — a Controls section (with its own
 * Save/Cancel), editable by the resource's owners and admins and read-only for
 * everyone else. The desktop `Resource.EditPanel` and the mobile Materials panel
 * both wrap this in their own shell, mirroring `EquipmentResourceEditBody`.
 */
export const MaterialResourceEditBody: React.FC<IProps> = ({
  recordId,
  record,
  templateId,
  resourceTypeId,
  runtimeContext,
  projectId,
  qrCodeUrl,
  isCreationMode,
  controlFields,
  controlCommands,
  onClose,
  onSave,
  keepOpenOnCreate,
  onTemplateChange,
  formSelector,
  onDelete,
  defaultExpandedSectionId = 'general',
  resourceSchema,
}) => {
  const showControls = resourceTypeSupportsControls(resourceTypeId)

  const body = (
    <>
      <ResourceEdit.Header />
      <ResourceEdit.Content>
        <ResourceEdit.QRCode />
        {isCreationMode && formSelector}
        <ResourceEdit.Sections
          sections={withDefaultExpanded(
            [
              generalResourceSection(),
              ...(showControls ? [controlsSection()] : []),
            ],
            defaultExpandedSectionId,
          )}
        />
      </ResourceEdit.Content>
      <ResourceEdit.Footer />
    </>
  )

  const card = (
    <ResourceEdit.Root
      recordId={recordId}
      record={record}
      templateId={templateId}
      resourceTypeId={resourceTypeId}
      runtimeContext={runtimeContext}
      onClose={onClose}
      onSave={onSave}
      keepOpenOnCreate={keepOpenOnCreate}
      onTemplateChange={onTemplateChange}
      onDelete={onDelete}
      qrCodeUrl={qrCodeUrl}
      resourceSchema={resourceSchema}
    >
      {body}
    </ResourceEdit.Root>
  )

  // The provider wraps Root so the card's single footer Save (in Root) can also
  // commit the controls draft — one Save/Cancel for the whole card.
  return showControls ? (
    <ResourceControlsTabProvider
      recordId={recordId}
      record={record}
      resourceTypeId={resourceTypeId}
      projectId={projectId || ''}
      controls={getResourceControlsForRecord(resourceTypeId, record)}
      isProjectLocation={isProjectLocationRecord(resourceTypeId, record)}
      controlCommands={controlCommands}
      controlFields={controlFields}
      onClose={onClose}
    >
      {card}
    </ResourceControlsTabProvider>
  ) : (
    card
  )
}
